/**
 * Deposit fee (Pfand / statiegeld) cart lines.
 *
 * Every deposit-bearing product line gets its own cart line carrying the
 * `_deposit_for` line item property, set to the parent line's variant id. The
 * separate line is what keeps product discounts off the deposit — Shopify sees
 * two unrelated lines — and the property is what stops Shopify from merging all
 * deposit lines into one. The deposit-fee validation function re-checks the same
 * pairing server-side and blocks checkout when it doesn't add up.
 *
 * Two sources feed a pass, and the split matters. The *rules* — which products
 * owe a deposit, which variant carries it, how many units a tray holds — come
 * from product metafields through snippets/deposit-fee-cart-data.liquid, because
 * the Ajax Cart API never returns metafields. The *cart* is read from /cart.js
 * on every pass. Line keys and quantities are exactly what goes stale between a
 * render and the write that follows it, and writing against a stale line key is
 * how a buyer ends up charged the wrong deposit.
 *
 * A pass is "updates first, then adds": /cart/update.js is absolute but cannot
 * carry line item properties, so a replacement line has to go through
 * /cart/add.js after the stale one has been zeroed. Each pass re-reads the cart,
 * so a buyer editing quantities mid-flight is picked up rather than overwritten.
 *
 * Holding checkout is the delicate part, because the cart stops being correct
 * the instant a buyer asks for a change — not when the theme gets around to
 * sending it. The theme debounces quantity edits and then swaps sections, which
 * leaves a window of several hundred milliseconds where the deposit is provably
 * wrong and nothing has happened yet. Three signals close it: the quantity
 * `change` event, which fires as the debounce starts; a count of cart writes in
 * flight, taken by wrapping fetch; and the theme's own cartUpdate. The cart only
 * counts as settled when a pass found nothing to do *and* no write is pending.
 *
 * Submits are intercepted and replayed once the cart settles. Wallet buttons
 * hand the cart straight to Shopify without a submit event, so those are covered
 * by the pointer-events lock in cart-drawer.css instead.
 *
 * Everything here fails open. A cart that cannot be reconciled — a network
 * error, a product with no deposit variant, a buyer faster than the retries —
 * is let through to the validation function, which blocks checkout and tells the
 * buyer to go back to the cart. A checkout button that never fires would be
 * worse than a deposit that gets caught one step later.
 */
if (!customElements.get('deposit-fee')) {
  class DepositFee extends HTMLElement {
    static #inFlight = null;
    static #pendingResync = false;
    static #debounceTimer = null;
    static #watchdogTimer = null;
    static #subscribed = false;
    static #gateInstalled = false;
    static #watchInstalled = false;
    static #releasedForm = null;

    /**
     * Whether the last pass found the cart already holding the deposit lines it
     * owes. Starts false: nothing has been checked yet, and a buyer can reach
     * the checkout button before the first pass finishes.
     */
    static #converged = false;

    /** Cart writes started by the theme and not yet answered. */
    static #pendingWrites = 0;

    /** Set when a render was caused by this reconciler rather than the buyer. */
    static #selfRender = false;

    static #BLOCKED_ATTR = 'data-deposit-fee-pending';
    static #SELF_HEADER = 'x-deposit-fee';
    static #CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
    static #DEBOUNCE_MS = 80;

    /** Longest a reconcile may hold checkout before the lock is force-released. */
    static #WATCHDOG_MS = 8000;

    /** Write, then re-read and diff again. Enough to settle a cart being edited. */
    static #MAX_PASSES = 3;

    /** Rounds the checkout gate waits before giving up and letting the buyer through. */
    static #MAX_SETTLE = 2;

    /** Longest the gate waits for the theme's own cart writes to come back. */
    static #DRAIN_MS = 3000;

    connectedCallback() {
      if (!DepositFee.#subscribed && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        DepositFee.#subscribed = true;
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.source === 'deposit-fee') return;
          DepositFee.#scheduleSync(true);
        });
      }

      DepositFee.#installCheckoutGate();
      DepositFee.#installCartWriteWatch();

      // A render this reconciler caused: the cart was just verified, so the pass
      // that follows only confirms it and must not flash the lock back on.
      const selfRender = DepositFee.#selfRender;
      DepositFee.#selfRender = false;
      DepositFee.#scheduleSync(!selfRender);
    }

    /**
     * @param {boolean} hold Whether the cart is now suspect and checkout should
     *   be held until the pass says otherwise.
     */
    static #scheduleSync(hold) {
      if (hold) {
        DepositFee.#converged = false;
        DepositFee.#setCheckoutBlocked(true);
      }

      if (DepositFee.#debounceTimer) clearTimeout(DepositFee.#debounceTimer);
      DepositFee.#debounceTimer = setTimeout(() => {
        DepositFee.#debounceTimer = null;
        DepositFee.#run();
      }, DepositFee.#DEBOUNCE_MS);
    }

    /** The cart is known good and nothing is on its way to changing it. */
    static #isSettled() {
      return DepositFee.#converged && DepositFee.#pendingWrites === 0;
    }

    /**
     * Product rules keyed by variant id, or null when deposits are switched off
     * for this market and there is nothing to enforce.
     *
     * An entry whose `deposit` is null owes nothing. A variant *missing* from
     * the map means the blob was rendered before that line was added, which is a
     * different thing entirely — see #plan.
     *
     * @returns {Map<string, {deposit: {depositVariantId: string, units: number}|null, isDeposit: boolean}>|null}
     */
    static #readRules() {
      const el = document.querySelector('deposit-fee');
      if (el == null) return null;

      const script = el.querySelector('script[type="application/json"]');
      if (script == null) return null;

      try {
        const data = JSON.parse(script.textContent);
        if (!data || data.active !== true || !Array.isArray(data.rules)) return null;

        const rules = new Map();
        for (const rule of data.rules) {
          const variantId = DepositFee.#toId(rule.variantId);
          if (!variantId) continue;

          const depositVariantId = DepositFee.#toId(rule.depositVariantId);
          const units = parseInt(rule.units, 10);
          const usable = depositVariantId != null && Number.isFinite(units) && units > 0;

          rules.set(variantId, {
            deposit: usable ? { depositVariantId, units } : null,
            isDeposit: rule.isDeposit === true,
          });
        }

        return rules;
      } catch (error) {
        console.error('[deposit-fee] could not parse rules', error);
        return null;
      }
    }

    /**
     * `_deposit_for` survives cart round-trips as a string while Liquid writes it
     * as a number, so ids are compared in their normalised string form.
     */
    static #toId(value) {
      if (value == null) return null;
      const match = String(value).match(/(\d+)$/);
      return match ? match[1] : null;
    }

    /**
     * Diffs the deposit lines in the live cart against what the rules require.
     *
     * `stale` reports that the cart holds a product line the rules say nothing
     * about. The diff is still correct for every other line, but the cart cannot
     * be declared settled, because the silence is indistinguishable from a
     * missing deposit until a matching render arrives.
     *
     * @returns {{updates: object, adds: Array, stale: boolean}}
     */
    static #plan(cart, rules) {
      const required = new Map();
      const existing = new Map();
      const updates = {};
      const adds = [];
      let stale = false;

      for (const item of cart.items) {
        const depositFor = DepositFee.#toId(item.properties && item.properties._deposit_for);
        if (depositFor) {
          existing.set(depositFor, item);
          continue;
        }

        const variantId = DepositFee.#toId(item.id);
        if (variantId == null) continue;

        if (!rules.has(variantId)) {
          stale = true;
          continue;
        }

        const entry = rules.get(variantId);

        // The deposit product on a line of its own. A legitimate deposit line
        // carries `_deposit_for` and was taken above, so reaching here means a
        // /cart/add permalink put it there, bypassing the theme. Charging a
        // deposit for nothing, so it goes — and ahead of the subscription check,
        // so the ordering matches the validation function exactly.
        if (entry.isDeposit) {
          updates[item.key] = 0;
          continue;
        }

        // Subscriptions carry no deposit today, matching the validation function.
        if (item.selling_plan_allocation) continue;

        if (entry.deposit == null) continue;

        required.set(variantId, {
          depositVariantId: entry.deposit.depositVariantId,
          quantity: item.quantity * entry.deposit.units,
          parentKey: item.key,
        });
      }

      for (const [parentId, want] of required) {
        const found = existing.get(parentId);

        if (found == null) {
          adds.push(DepositFee.#addLine(want, parentId));
          continue;
        }

        // Wrong deposit variant, e.g. after a VAT reconfiguration: drop the line
        // and add the correct one in the same pass.
        if (DepositFee.#toId(found.id) !== want.depositVariantId) {
          updates[found.key] = 0;
          adds.push(DepositFee.#addLine(want, parentId));
          continue;
        }

        if (found.quantity !== want.quantity) {
          updates[found.key] = want.quantity;
        }
      }

      // A deposit line whose parent is gone would overcharge the buyer.
      for (const [parentId, line] of existing) {
        if (!required.has(parentId)) updates[line.key] = 0;
      }

      return { updates, adds, stale };
    }

    /*
      `parent_line_key` nests the deposit under the line it belongs to. Shopify
      then draws it directly after its parent everywhere — cart, checkout, thank
      you page, order emails, admin — and deletes it when the parent goes, which
      is cleanup this file would otherwise have to get right on its own.

      `_deposit_for` stays. It is what the theme, the validation function and the
      diff above all match on, and it keeps the Ajax cart from merging deposits
      belonging to different parents into one line.
    */
    static #addLine(want, parentId) {
      return {
        id: parseInt(want.depositVariantId, 10),
        quantity: want.quantity,
        parent_line_key: want.parentKey,
        properties: { _deposit_for: parentId },
      };
    }

    /**
     * Starts a pass, or joins the one already running. Always returns a promise
     * that settles when the cart has been left alone again.
     */
    static #run() {
      if (DepositFee.#inFlight) {
        DepositFee.#pendingResync = true;
        return DepositFee.#inFlight;
      }

      DepositFee.#inFlight = DepositFee.#sync().finally(() => {
        DepositFee.#inFlight = null;

        if (DepositFee.#pendingResync) {
          DepositFee.#pendingResync = false;
          DepositFee.#scheduleSync(!DepositFee.#isSettled());
        } else {
          DepositFee.#setCheckoutBlocked(!DepositFee.#isSettled());
        }
      });

      return DepositFee.#inFlight;
    }

    static async #sync() {
      const rules = DepositFee.#readRules();
      if (rules == null) {
        DepositFee.#converged = true;
        return;
      }

      let wrote = false;

      try {
        for (let pass = 0; pass < DepositFee.#MAX_PASSES; pass += 1) {
          const cart = await DepositFee.#fetchCart();
          const { updates, adds, stale } = DepositFee.#plan(cart, rules);

          if (Object.keys(updates).length === 0 && adds.length === 0) {
            DepositFee.#converged = !stale;
            break;
          }

          if (Object.keys(updates).length > 0) {
            await DepositFee.#post(DepositFee.#url('cart_update_url', '/cart/update'), { updates });
          }
          if (adds.length > 0) {
            await DepositFee.#post(DepositFee.#url('cart_add_url', '/cart/add'), { items: adds });
          }

          wrote = true;
        }

        if (wrote) {
          // /cart/add.js answers with only the lines it added, so the whole cart
          // is read back for subscribers that expect a cart-shaped payload.
          const cartData = await DepositFee.#fetchCart();

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            DepositFee.#selfRender = true;
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'deposit-fee', cartData });
          }
        }
      } catch (error) {
        // Leaves the cart unsettled, so the gate tries once more and then hands
        // the buyer to the validation function.
        console.error('[deposit-fee]', error);
      }
    }

    /** Reconciles until the cart settles. @returns {Promise<boolean>} */
    static async #settle() {
      // A theme write already on the wire decides the quantities this pass would
      // diff against, so it has to land first or the diff is answered stale.
      const deadline = Date.now() + DepositFee.#DRAIN_MS;
      while (DepositFee.#pendingWrites > 0 && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      for (let attempt = 0; attempt < DepositFee.#MAX_SETTLE; attempt += 1) {
        if (DepositFee.#debounceTimer) {
          clearTimeout(DepositFee.#debounceTimer);
          DepositFee.#debounceTimer = null;
        }

        await DepositFee.#run();
        if (DepositFee.#isSettled()) return true;
      }

      return false;
    }

    /**
     * Notices a cart change at the earliest moment it can be known: the quantity
     * `change` event that starts the theme's debounce, and every cart write on
     * the wire. Without these the cart looks settled for as long as the theme
     * takes to send its request, which is exactly when a buyer clicks checkout.
     */
    static #installCartWriteWatch() {
      if (DepositFee.#watchInstalled) return;
      if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
      DepositFee.#watchInstalled = true;

      document.addEventListener(
        'change',
        (event) => {
          const el = event.target;
          if (el == null || typeof el.matches !== 'function') return;
          if (!el.matches('.quantity__input, input[name="quantity"]')) return;

          DepositFee.#converged = false;
          DepositFee.#setCheckoutBlocked(true);
        },
        true,
      );

      const original = window.fetch;

      window.fetch = function depositFeeCartWriteWatch(input, init) {
        if (!DepositFee.#isForeignCartWrite(input, init)) {
          return original.apply(this, arguments);
        }

        DepositFee.#pendingWrites += 1;
        DepositFee.#converged = false;
        DepositFee.#setCheckoutBlocked(true);

        return original.apply(this, arguments).finally(() => {
          DepositFee.#pendingWrites -= 1;
          DepositFee.#scheduleSync(true);
        });
      };
    }

    /** A cart write from the theme, as opposed to one of this reconciler's own. */
    static #isForeignCartWrite(input, init) {
      const method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      if (method !== 'POST') return false;

      const url = typeof input === 'string' ? input : (input && input.url) || '';
      if (!DepositFee.#CART_WRITE.test(url)) return false;

      const headers = (init && init.headers) || (input && input.headers);
      if (!headers) return true;

      if (typeof headers.get === 'function') return headers.get(DepositFee.#SELF_HEADER) == null;
      return !Object.keys(headers).some((key) => key.toLowerCase() === DepositFee.#SELF_HEADER);
    }

    static #installCheckoutGate() {
      if (DepositFee.#gateInstalled) return;
      DepositFee.#gateInstalled = true;

      // Capture phase on the document, so this runs before the theme's own
      // handlers and covers the drawer and the cart page with one listener.
      document.addEventListener('submit', DepositFee.#onSubmit, true);
    }

    static #onSubmit = (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      // Replay of a submit this gate already held. Let it reach the theme.
      if (form === DepositFee.#releasedForm) {
        DepositFee.#releasedForm = null;
        return;
      }

      // Only checkout submits are gated. Quantity edits post to the same form
      // and have to stay instant.
      const submitter = event.submitter;
      if (submitter == null || submitter.name !== 'checkout') return;

      if (DepositFee.#isSettled()) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      DepositFee.#settle().then(() => DepositFee.#releaseSubmit(form, submitter));
    };

    static #releaseSubmit(form, submitter) {
      DepositFee.#setCheckoutBlocked(false);
      DepositFee.#releasedForm = form;

      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitter);
        return;
      }

      // Clicking keeps the submitter's name in the payload, which plain
      // form.submit() drops — and with it the intent to check out.
      submitter.click();
    }

    /**
     * Fails open. Every exit from a pass releases the lock, and the watchdog
     * releases it if a request hangs, because a checkout button that stays dead
     * is worse than a missing deposit the validation function would catch.
     */
    static #setCheckoutBlocked(blocked) {
      if (document.body == null) return;

      if (DepositFee.#watchdogTimer) {
        clearTimeout(DepositFee.#watchdogTimer);
        DepositFee.#watchdogTimer = null;
      }

      if (!blocked) {
        document.body.removeAttribute(DepositFee.#BLOCKED_ATTR);
        return;
      }

      document.body.setAttribute(DepositFee.#BLOCKED_ATTR, '');
      DepositFee.#watchdogTimer = setTimeout(() => {
        DepositFee.#watchdogTimer = null;
        document.body.removeAttribute(DepositFee.#BLOCKED_ATTR);
      }, DepositFee.#WATCHDOG_MS);
    }

    static #post(url, body) {
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          // Marks the write as this reconciler's, so the watch above does not
          // treat it as a buyer edit and loop.
          [DepositFee.#SELF_HEADER]: '1',
        },
        body: JSON.stringify(body),
      }).then((response) => response.json());
    }

    static #fetchCart() {
      return fetch(DepositFee.#url('cart_url', '/cart')).then((response) => response.json());
    }

    static #url(route, fallback) {
      const base = (window.routes && window.routes[route]) || fallback;
      return base.indexOf('.js') !== -1 ? base : `${base}.js`;
    }
  }

  customElements.define('deposit-fee', DepositFee);
}
